// The comprehensive translations table inside the Valodas hub. Every
// translatable row (products with all their fields, categories, tags, grape
// varieties, blog posts) is an entry; expand one to edit all its fields per
// locale. Compact rows, type + status filters and search so translation work
// can be batched.

import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Search as SearchIcon, X, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";

type Field = { key: string; label: string; multiline: boolean; lv: string; en: string; ru: string };
type Entry = {
  kind: string;
  id: string;
  slug: string;
  label: string;
  fields: Field[];
  missingEn: boolean;
  missingRu: boolean;
};
type EntriesResp = { items: Entry[]; total: number; limit: number; offset: number };
type LocaleEdits = { en: Record<string, string>; ru: Record<string, string> };

const TYPES: Array<{ key: string; labelKey: string; fallback: string }> = [
  { key: "products", labelKey: "languages.products", fallback: "Products" },
  { key: "categories", labelKey: "languages.categories", fallback: "Categories" },
  { key: "tags", labelKey: "languages.tags", fallback: "Tags" },
  { key: "grapes", labelKey: "languages.grapes", fallback: "Grape varieties" },
  { key: "blog", labelKey: "languages.blog", fallback: "Blog" },
  { key: "ui", labelKey: "languages.uiStrings", fallback: "UI strings" },
];
const STATUSES: Array<{ key: string; labelKey: string; fallback: string }> = [
  { key: "all", labelKey: "languages.stAll", fallback: "All" },
  { key: "missing_any", labelKey: "languages.stMissingAny", fallback: "Missing any" },
  { key: "missing_en", labelKey: "languages.stMissingEn", fallback: "Missing EN" },
  { key: "missing_ru", labelKey: "languages.stMissingRu", fallback: "Missing RU" },
  { key: "complete", labelKey: "languages.stComplete", fallback: "Complete" },
];
const PAGE_SIZE = 25;

function seedEdits(items: Entry[]): Record<string, LocaleEdits> {
  const out: Record<string, LocaleEdits> = {};
  for (const e of items) {
    const en: Record<string, string> = {};
    const ru: Record<string, string> = {};
    for (const f of e.fields) {
      en[f.key] = f.en;
      ru[f.key] = f.ru;
    }
    out[e.id] = { en, ru };
  }
  return out;
}

export function TranslationsTable() {
  const { toast } = useToast();
  const t = useT();

  const [type, setType] = useState("products");
  const [status, setStatus] = useState("missing_any");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState<EntriesResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [edits, setEdits] = useState<Record<string, LocaleEdits>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => setPage(0), [type, status, q]);

  const load = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams({
      type,
      status,
      limit: String(PAGE_SIZE),
      offset: String(page * PAGE_SIZE),
    });
    if (q.trim()) qs.set("q", q.trim());
    try {
      const resp = await api<EntriesResp>(`/api/v1/admin/i18n/entries?${qs.toString()}`);
      setData(resp);
      setEdits(seedEdits(resp.items));
    } catch (err) {
      toast({
        title: t("languages.loadError", "Failed to load"),
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [type, status, q, page, t, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const setField = (id: string, loc: "en" | "ru", key: string, value: string) =>
    setEdits((prev) => {
      const cur: LocaleEdits = prev[id] ?? { en: {}, ru: {} };
      return { ...prev, [id]: { ...cur, [loc]: { ...cur[loc], [key]: value } } };
    });

  const isDirty = useCallback(
    (e: Entry) => {
      const ed = edits[e.id];
      if (!ed) return false;
      return e.fields.some((f) => (ed.en[f.key] ?? "") !== f.en || (ed.ru[f.key] ?? "") !== f.ru);
    },
    [edits],
  );

  const save = async (e: Entry) => {
    const ed = edits[e.id];
    if (!ed) return;
    setSavingId(e.id);
    try {
      await api(`/api/v1/admin/i18n/entries/${e.kind}/${e.id}`, {
        method: "PUT",
        body: JSON.stringify({ en: ed.en, ru: ed.ru }),
      });
      toast({ title: t("common.saved", "Saved") });
      await load();
    } catch (err) {
      toast({
        title: t("common.saveFailed", "Save failed"),
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setSavingId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  return (
    <section className="bg-card border border-border rounded-lg p-5 space-y-4">
      <h2 className="font-display text-lg font-semibold text-foreground">
        {t("languages.editorTitle", "Translations")}
      </h2>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1">
          {TYPES.map((ty) => (
            <button
              key={ty.key}
              type="button"
              onClick={() => setType(ty.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium border ${
                type === ty.key
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card text-muted-foreground border-border hover:text-foreground"
              }`}
            >
              {t(ty.labelKey, ty.fallback)}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-8 rounded-md border border-border bg-card px-2 text-xs text-foreground"
        >
          {STATUSES.map((s) => (
            <option key={s.key} value={s.key}>
              {t(s.labelKey, s.fallback)}
            </option>
          ))}
        </select>
        <div className="relative w-56">
          <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("common.search", "Search")}
            className="h-8 pl-8 pr-7 text-sm"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Entries */}
      <div className="border border-border rounded-lg divide-y divide-border">
        {loading ? (
          <div className="p-4 text-sm text-muted-foreground">{t("common.loading", "Loading...")}</div>
        ) : data && data.items.length > 0 ? (
          data.items.map((e) => (
            <EntryBlock
              key={e.id}
              entry={e}
              edits={edits[e.id]}
              expanded={expanded.has(e.id)}
              dirty={isDirty(e)}
              saving={savingId === e.id}
              onToggle={() =>
                setExpanded((prev) => {
                  const next = new Set(prev);
                  next.has(e.id) ? next.delete(e.id) : next.add(e.id);
                  return next;
                })
              }
              onField={setField}
              onSave={() => save(e)}
            />
          ))
        ) : (
          <div className="p-4 text-sm text-muted-foreground">
            {t("languages.noEntries", "No entries match these filters.")}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {(data?.total ?? 0)} {t("languages.entries", "entries")}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              {t("common.prev", "Previous")}
            </Button>
            <span className="text-xs text-muted-foreground">
              {page + 1} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              {t("common.next", "Next")}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function EntryBlock({
  entry,
  edits,
  expanded,
  dirty,
  saving,
  onToggle,
  onField,
  onSave,
}: {
  entry: Entry;
  edits: LocaleEdits | undefined;
  expanded: boolean;
  dirty: boolean;
  saving: boolean;
  onToggle: () => void;
  onField: (id: string, loc: "en" | "ru", key: string, value: string) => void;
  onSave: () => void;
}) {
  const t = useT();
  return (
    <div>
      <div className="flex items-center gap-2 px-3 py-2">
        <button type="button" onClick={onToggle} className="flex items-center gap-2 flex-1 min-w-0 text-left">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
          )}
          <span className="text-sm text-foreground truncate">{entry.label}</span>
        </button>
        <StatusDot on={!entry.missingEn} label="EN" />
        <StatusDot on={!entry.missingRu} label="RU" />
        {dirty && (
          <Button size="sm" className="h-7" disabled={saving} onClick={onSave}>
            {saving ? t("common.saving", "Saving...") : t("common.save", "Save")}
          </Button>
        )}
      </div>

      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          <div className="grid grid-cols-[120px_1fr_1fr] gap-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground px-1">
            <span />
            <span>EN</span>
            <span>RU</span>
          </div>
          {entry.fields.map((f) => (
            <div key={f.key} className="grid grid-cols-[120px_1fr_1fr] gap-2 items-start">
              <div className="pt-1.5 flex items-center gap-1.5">
                <span className="text-xs font-medium text-foreground">{f.label}</span>
                {f.lv ? (
                  <HoverCard openDelay={100} closeDelay={50}>
                    <HoverCardTrigger asChild>
                      <button
                        type="button"
                        className="shrink-0 rounded border border-border px-1 text-[9px] font-semibold uppercase leading-4 text-muted-foreground hover:border-primary hover:text-primary cursor-help"
                        aria-label="Latviešu teksts"
                      >
                        LV
                      </button>
                    </HoverCardTrigger>
                    <HoverCardContent side="top" align="start" className="max-w-sm text-xs whitespace-pre-wrap">
                      {f.lv}
                    </HoverCardContent>
                  </HoverCard>
                ) : (
                  <span className="text-[9px] text-muted-foreground/40">—</span>
                )}
              </div>
              <LocaleInput
                value={edits?.en[f.key] ?? ""}
                multiline={f.multiline}
                onChange={(v) => onField(entry.id, "en", f.key, v)}
              />
              <LocaleInput
                value={edits?.ru[f.key] ?? ""}
                multiline={f.multiline}
                onChange={(v) => onField(entry.id, "ru", f.key, v)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LocaleInput({
  value,
  multiline,
  onChange,
}: {
  value: string;
  multiline: boolean;
  onChange: (v: string) => void;
}) {
  const cls =
    "w-full bg-card border border-border rounded px-2 py-1 text-xs text-foreground outline-none focus:border-primary";
  return multiline ? (
    <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} className={cls + " resize-y"} />
  ) : (
    <input value={value} onChange={(e) => onChange(e.target.value)} className={cls} />
  );
}

function StatusDot({ on, label }: { on: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium ${
        on ? "text-green-600" : "text-muted-foreground/50"
      }`}
      title={on ? `${label} complete` : `${label} missing`}
    >
      {on ? <Check className="h-3 w-3" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      {label}
    </span>
  );
}
